import type { AuditEntryDetailDto, AuditEntryDto, AuditPageDto } from "~/lib/api";
import {
  getSessionAuditEntry as sdkGetSessionAuditEntry,
  listSessionAudit as sdkListSessionAudit,
} from "~/lib/api";
import type { FetchResult } from "~/lib/patients";

export type { AuditEntryDetailDto, AuditEntryDto, AuditPageDto };

/**
 * Single page of the audit timeline (E8.2). 404 → caller renders not-found.
 * `patientName` (V2-E9.2) narrows the page to one patient by a case-insensitive
 * substring of the denormalised name; it must be re-sent on every page since
 * the cursor doesn't carry it.
 */
export async function fetchSessionAuditPage(
  sessionId: string,
  options: { limit?: number; before?: string; patientName?: string } = {},
): Promise<FetchResult<AuditPageDto>> {
  const result = await sdkListSessionAudit({
    path: { sessionId },
    query: {
      limit: options.limit,
      before: options.before,
      patientName: options.patientName,
    },
  });
  if (result.response.status === 404) return { status: "not-found" };
  if (result.error || !result.data) {
    return {
      status: "error",
      message: `Couldn't load the audit log (${result.response.status}).`,
    };
  }
  return { status: "ok", data: result.data };
}

/**
 * V2-E9.1 (HAN-69) — fetch one entry's full structured detail when the viewer
 * expands a row. The list endpoint omits `detail`; this pulls it on demand so a
 * doctor can see the before/after of an action. 404 → the entry vanished (or the
 * caller lost access); the row treats that as a load failure rather than tearing
 * down the whole timeline.
 */
export async function fetchSessionAuditEntry(
  sessionId: string,
  entryId: string,
): Promise<FetchResult<AuditEntryDetailDto>> {
  const result = await sdkGetSessionAuditEntry({
    path: { sessionId, entryId },
  });
  if (result.response.status === 404) return { status: "not-found" };
  if (result.error || !result.data) {
    return {
      status: "error",
      message: `Couldn't load the entry detail (${result.response.status}).`,
    };
  }
  return { status: "ok", data: result.data };
}
